<div [ngClass]="tableSectionClass">
  <div [ngClass]="tableWrapperClass" [style.maxHeight]="maxHeight || null">
    <table [ngClass]="tableClass">
      <!-- Table Header -->
      <thead>
        <tr>
          <ng-container *ngFor="let col of columns">
            <th
              [ngClass]="getHeaderClass(col)"
              [style.width]="col.width || null"
              (click)="onSort(col)"
            >
              <div class="d-flex align-items-center" [class.justify-content-center]="col.align === 'center'" [class.justify-content-end]="col.align === 'end' || col.align === 'right'">
                <span>{{ col.label }}</span>
                <!-- Sort Icons -->
                <span *ngIf="col.sortable" class="ms-1 d-inline-flex sort-icon-wrapper">
                  <svg
                    *ngIf="sortActive !== (col.sortKey || col.key)"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="m7 15 5 5 5-5M7 9l5-5 5 5" />
                  </svg>
                  <svg
                    *ngIf="sortActive === (col.sortKey || col.key) && sortDirection === 'asc'"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="m18 15-6-6-6 6" />
                  </svg>
                  <svg
                    *ngIf="sortActive === (col.sortKey || col.key) && sortDirection === 'desc'"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </span>
              </div>
            </th>
          </ng-container>
        </tr>
      </thead>

      <!-- Table Body -->
      <tbody>
        <!-- Data Rows -->
        <ng-container *ngIf="data && data.length > 0">
          <tr
            *ngFor="let row of data; let rowIndex = index"
            [ngClass]="getRowClass(row, rowIndex)"
            (click)="onRowClick(row, rowIndex, $event)"
          >
            <ng-container *ngFor="let col of columns; let colIndex = index">
              <td
                [ngClass]="getCellClass(row, col, rowIndex)"
                [style.width]="col.width || null"
              >
                <!-- Custom Template Outlet -->
                <ng-container *ngIf="getTemplate(col.key) as customTpl; else defaultCell">
                  <ng-container
                    *ngTemplateOutlet="
                      customTpl;
                      context: {
                        row: row,
                        value: getCellValue(row, col, rowIndex),
                        index: rowIndex,
                        col: col
                      }
                    "
                  ></ng-container>
                </ng-container>

                <!-- Built-in Cell Rendering -->
                <ng-template #defaultCell>
                  <!-- Currency Pipe -->
                  <ng-container *ngIf="col.type === 'currency'">
                    {{ getCellValue(row, col, rowIndex) | divide }}
                  </ng-container>

                  <!-- Date Pipe -->
                  <ng-container *ngIf="col.type === 'date'">
                    {{ getCellValue(row, col, rowIndex) | date : (col.dateFormat || 'MMM dd, yyyy') }}
                  </ng-container>

                  <!-- Status Badge -->
                  <ng-container *ngIf="col.type === 'badge'">
                    <span
                      class="status-badge"
                      [ngClass]="getBadgeClass(row, col, rowIndex)"
                    >
                      {{ getCellValue(row, col, rowIndex) }}
                    </span>
                  </ng-container>

                  <!-- Standard Text -->
                  <ng-container *ngIf="!col.type || col.type === 'text'">
                    {{ getCellValue(row, col, rowIndex) }}
                  </ng-container>
                </ng-template>
              </td>
            </ng-container>
          </tr>
        </ng-container>

        <!-- Empty State Row -->
        <tr *ngIf="(!data || data.length === 0) && showEmptyState">
          <td [attr.colspan]="columns.length" class="text-center py-5">
            <ng-container *ngIf="emptyTemplate; else defaultEmptyState">
              <ng-container *ngTemplateOutlet="emptyTemplate"></ng-container>
            </ng-container>
            <ng-template #defaultEmptyState>
              <div class="empty-state-content">
                <div class="empty-state-icon mb-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="text-muted opacity-50"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" x2="8" y1="13" y2="13" />
                    <line x1="16" x2="8" y1="17" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                </div>
                <h4 class="text-dark font-semibold">{{ emptyMessage }}</h4>
                <p class="text-muted small">{{ emptySubMessage }}</p>
              </div>
            </ng-template>
          </td>
        </tr>
      </tbody>

      <!-- Optional Table Footer -->
      <tfoot *ngIf="showFooter">
        <ng-container *ngIf="footerTemplate; else defaultFooter">
          <ng-container
            *ngTemplateOutlet="footerTemplate; context: { footerData: footerData, columns: columns }"
          ></ng-container>
        </ng-container>

        <ng-template #defaultFooter>
          <tr class="table-footer-row">
            <ng-container *ngFor="let col of columns; let colIndex = index">
              <ng-container *ngIf="!col.skipFooter">
                <td
                  [ngClass]="getFooterClass(col, colIndex)"
                  [attr.colspan]="col.footerColspan || null"
                  [style.width]="col.width || null"
                >
                  <ng-container *ngIf="getFooterValue(col, colIndex) !== ''; else emptyFooterCell">
                    <ng-container
                      *ngIf="
                        (col.footerType === 'currency' || (!col.footerType && col.type === 'currency')) &&
                          getFooterValue(col, colIndex) !== footerLabel;
                        else rawFooterValue
                      "
                    >
                      {{ getFooterValue(col, colIndex) | divide }}
                    </ng-container>
                    <ng-template #rawFooterValue>
                      {{ getFooterValue(col, colIndex) }}
                    </ng-template>
                  </ng-container>
                  <ng-template #emptyFooterCell></ng-template>
                </td>
              </ng-container>
            </ng-container>
          </tr>
        </ng-template>
      </tfoot>
    </table>
  </div>
</div>
